import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import { API_URLS } from "../../config";
import { apiRequest } from "../../utils/apiRequest";
import AccessDenied from "../../Components/AccessDenied";
import { useToast } from "../../Components/Toast";
import {
  FiEdit, FiPlus, FiEye, FiTrash2, FiChevronUp, FiChevronDown, FiRefreshCw, FiCheck, FiX, FiUploadCloud, FiMapPin
} from "react-icons/fi";
import { LiaUsersSolid } from "react-icons/lia";
import MasterUploadModal from "../../Components/MasterUploadModal";
import "./Vendors.css";
import Competency from "../../Components/Competency";
import { FaUserTie, FaChevronUp, FaChevronDown } from "react-icons/fa";
import PhoneField from "../../Components/PhoneField";
import Select, { createFilter } from "react-select";
import { Country, State, City } from "country-state-city";
import FilterPanel, { FilterField, filterSelectStyles } from "../../Components/FilterPanel";


// Location dropdowns match from the start of the name, so "oman" finds Oman, not Romania.
const matchFromStart = createFilter({ matchFrom: "start" });

/* ── helpers ─────────────────────────────────────────────────── */
const EMPTY_FORM = {
  vendorName: "",
  country: "",
  state: "",
  city: "",
  address: "",
  contactPersonName: "",
  contactNumber: "",
  emailId: "",
  competency: {},
};

const Vendors = () => {
  const { token, user } = useAuth();
  const toast = useToast();

  /* list state */
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  /* selection state */
  const [selectedIds, setSelectedIds] = useState([]);
  const [isTableCollapsed, setIsTableCollapsed] = useState(false);

  /* filter state */
  const [search, setSearch] = useState("");
  const [fCountry, setFCountry] = useState("");
  const [fState, setFState] = useState("");
  const [fCity, setFCity] = useState("");

  /* modal state */
  const [modal, setModal] = useState(null); // "add" | "edit" | "view"
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isBasicCollapsed, setIsBasicCollapsed] = useState(false);
  const [isAddressCollapsed, setIsAddressCollapsed] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  /* option state */
  const allCountries = useMemo(() => Country.getAllCountries(), []);

  const selectedCountryIso = useMemo(() => {
    const c = allCountries.find(c => c.name === formData?.country);
    return c ? c.isoCode : "";
  }, [allCountries, formData?.country]);

  const stateOptions = useMemo(() => {
    if (!selectedCountryIso) return [];
    return State.getStatesOfCountry(selectedCountryIso);
  }, [selectedCountryIso]);

  const selectedStateIso = useMemo(() => {
    if (!selectedCountryIso || !formData?.state) return "";
    const s = stateOptions.find(st => st.name === formData?.state);
    return s ? s.isoCode : "";
  }, [stateOptions, formData?.state, selectedCountryIso]);

  const cityOptions = useMemo(() => {
    if (!selectedCountryIso || !selectedStateIso) return [];
    return City.getCitiesOfState(selectedCountryIso, selectedStateIso);
  }, [selectedCountryIso, selectedStateIso]);

  /* filter-panel options — states/cities for whatever country/state is currently picked */
  const isoOfCountry = (name) => allCountries.find((c) => c.name === name)?.isoCode || "";
  const statesOf = (country) => {
    const iso = isoOfCountry(country);
    return iso ? State.getStatesOfCountry(iso) : [];
  };
  const citiesOf = (country, state) => {
    const iso = isoOfCountry(country);
    const st = statesOf(country).find((s) => s.name === state);
    return iso && st ? City.getCitiesOfState(iso, st.isoCode) : [];
  };

  /* ── fetch list ─────────────────────────────────────────────── */
  const fetchRows = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit });
    if (search) params.set("search", search);
    if (fCountry) params.set("country", fCountry);
    if (fState) params.set("state", fState);
    if (fCity) params.set("city", fCity);

    const result = await apiRequest(`${API_URLS.VENDORS}?${params}`, {}, token);
    if (!result) return;
    if (result.denied) { setDenied(result); setLoading(false); return; }
    if (result.ok) {
      setRows(result.data.rows || []);
      setTotal(result.data.total || 0);
      setSelectedIds([]);
    }
    setLoading(false);
  }, [token, page, search, fCountry, fState, fCity, limit]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  /* ── Selection ── */
  const toggleSelectRow = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === rows.length && rows.length > 0) setSelectedIds([]);
    else setSelectedIds(rows.map(r => r.id));
  };

  /* modal helpers ──────────────────────────────────────────── */
  const initialFormSnapshotRef = useRef(null);
  const openAdd = () => { setFormData(EMPTY_FORM); initialFormSnapshotRef.current = JSON.stringify(EMPTY_FORM); setModal("add"); };
  const openEdit = async () => {
    if (selectedIds.length !== 1) return;
    const item = rows.find(r => r.id === selectedIds[0]);
    if (item) {
      setFormData({ ...item });
      initialFormSnapshotRef.current = JSON.stringify({ ...item });
      setModal("edit");
    }
  };
  const openView = async () => {
    if (selectedIds.length !== 1) return;
    const item = rows.find(r => r.id === selectedIds[0]);
    if (item) {
      setFormData(item);
      setModal("view");
    }
  };
  const closeModal = () => { setModal(null); setFormData(EMPTY_FORM); };

  // Unsaved-changes guard — only relevant for add/edit, never for read-only view
  const isFormDirty = () => modal !== "view" && initialFormSnapshotRef.current !== null && JSON.stringify(formData) !== initialFormSnapshotRef.current;
  const requestCloseModal = () => {
    if (isFormDirty()) {
      toast.confirm(
        "You have unsaved changes. Do you want to save before closing?",
        () => handleSave(),
        () => closeModal()
      );
    } else {
      closeModal();
    }
  };

  const handleFormChange = e => {
    const { name, value } = e.target;
    setFormData(f => ({ ...f, [name]: value }));
  };

  const handleCompetencyChange = (newVal) => {
    setFormData(f => ({ ...f, competency: newVal }));
  };

  /* ── save ───────────────────────────────────────────────────── */
  const handleSave = async (e) => {
    if (e) e.preventDefault();
    if (!formData.country) {
      toast.warning("Country is required!");
      return;
    }
    setSaving(true);
    const isEdit = modal === "edit";
    const url = isEdit ? `${API_URLS.VENDORS}/${selectedIds[0]}` : API_URLS.VENDORS;
    const method = isEdit ? "PUT" : "POST";
    const result = await apiRequest(url, { method, body: formData }, token);
    setSaving(false);
    if (result?.ok) {
      toast.success("Saved successfully!");
      closeModal();
      fetchRows();
    } else toast.error(result?.data?.message || "Failed");
  };

  /* ── delete ─────────────────────────────────────────────────── */
  const handleDelete = () => {
    if (selectedIds.length === 0) return;
    const msg = selectedIds.length === 1
      ? "Delete this vendor?"
      : `Delete ${selectedIds.length} selected vendors?`;

    toast.deleteConfirm(msg, async () => {
      setLoading(true);
      let successCount = 0;
      for (const id of selectedIds) {
        const res = await apiRequest(`${API_URLS.VENDORS}/${id}`, { method: "DELETE" }, token);
        if (res?.ok) successCount++;
      }
      setLoading(false);
      if (successCount > 0) {
        toast.success(`Deleted ${successCount} items.`);
        fetchRows();
      } else toast.error("Failed to delete items.");
    });
  };

  /* ── export ─────────────────────────────────────────────────── */
  const handleExport = async () => {
    const res = await fetch(`${API_URLS.VENDORS}/export`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) {
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Vendors_${Date.now()}.xlsx`;
      a.click();
    }
  };

  const pages = Math.ceil(total / limit);

  if (denied) return (
    <div className="vendor-page">
      <div className="management-card">
        <AccessDenied message={denied.message} required={denied.required} />
      </div>
    </div>
  );

  return (
    <div className="vendor-page">

      {/* ── Action Bar ── */}
      <div className="vendor-action-bar">
        <button className="new-user-btn" onClick={openAdd}><FiPlus /> New Vendor</button>
        <div style={{ width: 1, height: 24, background: "#ccc", margin: "0 8px" }} />
        <button className="action-btn view-btn" disabled={selectedIds.length !== 1} onClick={openView}><FiEye /> View</button>
        <button className="action-btn edit-btn" disabled={selectedIds.length !== 1} onClick={openEdit}><FiEdit /> Edit</button>
        <button className="action-btn delete-btn" disabled={selectedIds.length === 0} onClick={handleDelete}><FiTrash2 /> Delete</button>
        {['global_superadmin', 'company_admin'].includes(user?.role) && (
          <>
            <button className="action-btn export-btn" onClick={handleExport}><FiRefreshCw size={13} /> Export</button>
            <button className="action-btn upload-btn" onClick={() => setIsUploadOpen(true)}><FiUploadCloud /> Upload</button>
          </>
        )}
        <FilterPanel
          title="Filter vendors"
          width={320}
          columns={1}
          values={{ search, country: fCountry, state: fState, city: fCity }}
          emptyValues={{ search: "", country: "", state: "", city: "" }}
          onChange={(v) => { setSearch(v.search); setFCountry(v.country); setFState(v.state); setFCity(v.city); setPage(1); }}
        >
          {(filters, set) => (
            <>
              <FilterField label="Search" wide>
                <input autoFocus placeholder="Search vendor..." value={filters.search}
                  onChange={(e) => set({ search: e.target.value })} />
              </FilterField>
              <FilterField label="Country">
                <Select
                  options={allCountries.map(c => ({ value: c.name, label: c.name }))}
                  filterOption={matchFromStart}
                  value={filters.country ? { value: filters.country, label: filters.country } : null}
                  onChange={(s) => set({ country: s ? s.value : "", state: "", city: "" })}
                  placeholder="All Countries"
                  isClearable
                  styles={filterSelectStyles}
                />
              </FilterField>
              <FilterField label="State">
                <Select
                  options={statesOf(filters.country).map(s => ({ value: s.name, label: s.name }))}
                  filterOption={matchFromStart}
                  value={filters.state ? { value: filters.state, label: filters.state } : null}
                  onChange={(s) => set({ state: s ? s.value : "", city: "" })}
                  placeholder="All States"
                  isDisabled={!filters.country}
                  isClearable
                  styles={filterSelectStyles}
                />
              </FilterField>
              <FilterField label="City">
                <Select
                  options={citiesOf(filters.country, filters.state).map(c => ({ value: c.name, label: c.name }))}
                  filterOption={matchFromStart}
                  value={filters.city ? { value: filters.city, label: filters.city } : null}
                  onChange={(s) => set({ city: s ? s.value : "" })}
                  placeholder="All Cities"
                  isDisabled={!filters.state}
                  isClearable
                  styles={filterSelectStyles}
                />
              </FilterField>
            </>
          )}
        </FilterPanel>
      </div>

      {/* ── Table Card ── */}
      <div className="management-card">
        <div className="management-header" onClick={() => setIsTableCollapsed(!isTableCollapsed)}>
          <div className="header-title">
            <LiaUsersSolid size={18} />
            <h2>Vendor Master</h2>
            <span className="vendor-badge">{total}</span>
          </div>
          <div>{isTableCollapsed ? <FiChevronDown /> : <FiChevronUp />}</div>
        </div>

        {!isTableCollapsed && (
          <div className="vendor-table-container">
            {loading && rows.length === 0 ? <div style={{ padding: 20 }}>Loading...</div> : (
              <table className="vendor-table">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>
                      <input
                        type="checkbox"
                        checked={rows.length > 0 && selectedIds.length === rows.length}
                        onChange={toggleSelectAll}
                      />
                    </th>
                    <th style={{ width: 80 }}>SR. NO.</th>
                    <th>VENDOR NAME</th>
                    <th>COUNTRY</th>
                    <th>STATE</th>
                    <th>CITY</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan={6} style={{ textAlign: "center", padding: 40 }}>No vendors found</td></tr>
                  ) : rows.map((r, i) => (
                    <tr
                      key={r.id || i}
                      className={selectedIds.includes(r.id) ? "selected" : ""}
                      onClick={() => toggleSelectRow(r.id)}
                    >
                      <td onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(r.id)}
                          onChange={() => toggleSelectRow(r.id)}
                        />
                      </td>
                      <td>{r.srNo}</td>
                      <td>{r.vendorName}</td>
                      <td>{r.country}</td>
                      <td>{r.state}</td>
                      <td>{r.city}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Pagination */}
            {total > 0 && (
              <div className="cust-pagination">
                <select
                  value={limit}
                  onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
                  style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: 4, fontSize: 13, cursor: 'pointer' }}
                >
                  {[25, 50, 75, 100].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <span className="cust-page-info">Page {page} of {Math.max(1, pages)}</span>
                <button className="cust-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</button>
                <button className="cust-btn" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next</button>
              </div>
            )}
          </div>
        )}
      </div>

      {modal && (
        <div className="vendor-modal-overlay" onClick={requestCloseModal}>
          <div className="vendor-modal" onClick={e => e.stopPropagation()} style={{ width: "85%", maxWidth: "100%" }}>
            <div className="cust-modal-header">
              <span style={{ fontWeight: 600 }}>{modal.toUpperCase()} VENDOR</span>
              <button onClick={requestCloseModal} className="cust-modal-close"><FiX size={16} /></button>
            </div>
            <div style={{ padding: 30, overflowX: "auto", maxHeight: "65vh" }}>
              <form onSubmit={handleSave}>
                <div className="Vendor-Wrapper">
                  <div className="vendor-basic" style={{ border: '1px solid #ccc', flex: 1 }}>
                    <div className="vendor-basic-header" onClick={() => setIsBasicCollapsed(!isBasicCollapsed)} style={{ cursor: 'pointer' }}>
                      <span style={{ display: "flex", alignItems: "center", gap: "5px" }}> <FaUserTie size={16} />Basic Details </span>
                      <span>{isBasicCollapsed ? <FaChevronDown /> : <FaChevronUp />}</span>
                    </div>
                    {!isBasicCollapsed && (
                      <div className="vendor-view-grid" style={{ padding: 20, gridTemplateColumns: "repeat(2, 1fr)" }}>
                        <div className="vendor-view-field vendor-full">
                          <label className="vendor-view-label">Vendor Name *</label>
                          <input name="vendorName" className="vendor-filter-input" value={formData.vendorName} onChange={handleFormChange} required disabled={modal === 'view'} style={{}} />
                        </div>
                        <div className="vendor-view-field">
                          <label className="vendor-view-label">Contact Person</label>
                          <input name="contactPersonName" className="vendor-filter-input" value={formData.contactPersonName} onChange={handleFormChange} disabled={modal === 'view'} />
                        </div>
                        <div className="vendor-view-field">
                          <PhoneField
                            label="Contact Number"
                            value={formData.contactNumber}
                            onChange={(val) => setFormData(prev => ({ ...prev, contactNumber: val }))}
                            disabled={modal === 'view'}
                          />
                        </div>
                        <div className="vendor-view-field">
                          <label className="vendor-view-label">Email ID</label>
                          <input name="emailId" type="email" className="vendor-filter-input" value={formData.emailId} onChange={handleFormChange} disabled={modal === 'view'} />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="vendor-address" style={{ border: '1px solid #ccc', flex: 1 }}>
                    <div className="vendor-basic-header" onClick={() => setIsAddressCollapsed(!isAddressCollapsed)} style={{ cursor: 'pointer' }}>
                      <span style={{ display: "flex", alignItems: "center", gap: "5px" }}> <FiMapPin /> Location Details </span>
                      <span style={{ display: "flex", alignItems: "center", gap: "5px" }}>{isAddressCollapsed ? <FaChevronDown /> : <FaChevronUp />}</span>
                    </div>

                    {!isAddressCollapsed && (
                      <div className="vendor-view-grid" style={{ padding: 20, gridTemplateColumns: "repeat(2, 1fr)" }}>
                        <div className="vendor-view-field">
                          <label className="vendor-view-label">Country *</label>
                          {modal === 'view' ? (
                            <input className="vendor-filter-input" value={formData.country || ""} disabled />
                          ) : (
                            <Select
                              options={allCountries.map(c => ({ value: c.name, label: c.name }))}
                              filterOption={matchFromStart}
                              value={formData.country ? { value: formData.country, label: formData.country } : null}
                              onChange={(selected) => setFormData(f => ({ ...f, country: selected ? selected.value : "", state: "", city: "" }))}
                              placeholder="Select Country"
                              isClearable
                              styles={filterSelectStyles}
                            />
                          )}
                        </div>
                        <div className="vendor-view-field">
                          <label className="vendor-view-label">State</label>
                          {modal === 'view' ? (
                            <input className="vendor-filter-input" value={formData.state || ""} disabled />
                          ) : (
                            <Select
                              options={stateOptions.map(s => ({ value: s.name, label: s.name }))}
                              filterOption={matchFromStart}
                              value={formData.state ? { value: formData.state, label: formData.state } : null}
                              onChange={(selected) => setFormData(f => ({ ...f, state: selected ? selected.value : "", city: "" }))}
                              placeholder="Select State"
                              isDisabled={!formData.country}
                              isClearable
                              styles={filterSelectStyles}
                            />
                          )}
                        </div>
                        <div className="vendor-view-field">
                          <label className="vendor-view-label">City</label>
                          {modal === 'view' ? (
                            <input className="vendor-filter-input" value={formData.city || ""} disabled />
                          ) : (
                            <Select
                              options={cityOptions.map(c => ({ value: c.name, label: c.name }))}
                              filterOption={matchFromStart}
                              value={formData.city ? { value: formData.city, label: formData.city } : null}
                              onChange={(selected) => setFormData(f => ({ ...f, city: selected ? selected.value : "" }))}
                              placeholder="Select City"
                              isDisabled={!formData.state}
                              isClearable
                              styles={filterSelectStyles}
                            />
                          )}
                        </div>

                        <div className="vendor-view-field vendor-full">
                          <label className="vendor-view-label">Address *</label>
                          <textarea name="address" className="vendor-filter-input" value={formData.address} onChange={handleFormChange} required disabled={modal === 'view'} style={{ height: 60 }} />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <Competency
                  value={formData.competency}
                  onChange={handleCompetencyChange}
                  readOnly={modal === 'view'}
                />

                <div className="form-footer" style={{ marginTop: 30, textAlign: "right", display: "flex", justifyContent: "flex-end", gap: 10 }}>
                  {modal !== 'view' && (
                    <button type="submit" className="save-btn" disabled={saving}><FiCheck /> Save</button>
                  )}
                  <button type="button" className="cancel-btn" onClick={requestCloseModal}>{modal === 'view' ? "Close" : "Cancel"}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      <MasterUploadModal
        isOpen={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        title="Upload Vendor"
        templateUrl={`${API_URLS.VENDORS}/template`}
        uploadUrl={`${API_URLS.VENDORS}/upload`}
        instructions={[
          "Vendor Name, Country, and Address are mandatory fields.",
          "Email field must be unique if provided."
        ]}
        onSuccess={fetchRows}
        token={token}
      />
    </div>
  );
};

export default Vendors;